import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  TextField
} from '@mui/material'
import {
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  AttachMoney as MoneyIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { billingApi, institutionService } from '../../services/api'
import { BillingDashboardResponse, Institution, Contract } from '../../types/api'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'

const BillingDashboard: React.FC = () => {
  const { user } = useAuth()
  const [dashboardData, setDashboardData] = useState<BillingDashboardResponse | null>(null)
  const [loading, setLoading] = useState(false)
  
  // Filters
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date()
    date.setDate(1) // First day of current month
    return date.toISOString().split('T')[0]
  })
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0])
  const [institutionFilter, setInstitutionFilter] = useState('')
  
  // Pagination state
  const [page, setPage] = useState(0) // MUI TablePagination uses 0-based index
  const [rowsPerPage, setRowsPerPage] = useState(20)
  
  // Dropdown data
  const [institutions, setInstitutions] = useState<Institution[]>([])

  // Load dashboard data
  const loadDashboard = async (resetPage = false) => {
    try {
      setLoading(true)
      const currentPage = resetPage ? 0 : page
      if (resetPage) setPage(0)
      
      const response = await billingApi.getBillingDashboard({
        from_date: fromDate,
        to_date: toDate,
        institution_id: institutionFilter || undefined,
        page: currentPage + 1, // API uses 1-based index
        size: rowsPerPage
      })
      
      setDashboardData(response)
    } catch (error) {
      console.error('Error loading dashboard:', error)
      toast.error('Eroare la încărcarea dashboard-ului')
    } finally {
      setLoading(false)
    }
  }

  // Load institutions for admin/operator
  const loadInstitutions = async () => {
    try {
      const response = await institutionService.getInstitutions({ limit: 200 })
      setInstitutions(response.institutions)
    } catch (error) {
      console.error('Error loading institutions:', error)
    }
  }

  // Load institutions for medic (from contracts - includes remote/guard institutions without PACS)
  const loadMedicInstitutions = async () => {
    try {
      // Obține toate contractele medicului (active + inactive) pentru a vedea toate instituțiile
      // unde are/a avut contract (inclusiv gărzi remote fără PACS intern)
      const response = await billingApi.getContracts({ size: 100 })
      
      // Extrage instituțiile unice din contracte (API returnează institution_id și institution_name)
      const uniqueInstitutions = new Map<string, Institution>()
      response.contracts.forEach(contract => {
        if (contract.institution_id && contract.institution_name && !uniqueInstitutions.has(contract.institution_id)) {
          uniqueInstitutions.set(contract.institution_id, {
            id: contract.institution_id,
            name: contract.institution_name,
            tenant_id: contract.tenant_id || '',
            active: true,
            created_at: ''
          })
        }
      })
      
      setInstitutions(Array.from(uniqueInstitutions.values()))
    } catch (error) {
      console.error('Error loading medic institutions:', error)
      toast.error('Eroare la încărcarea instituțiilor')
    }
  }

  useEffect(() => {
    loadDashboard(true) // Reset page when filters change
  }, [fromDate, toDate, institutionFilter])
  
  useEffect(() => {
    loadDashboard() // Load when page/size changes
  }, [page, rowsPerPage])

  useEffect(() => {
    // Load institutions based on user role
    if (user?.role === 'medic') {
      loadMedicInstitutions()
    } else if (user?.role && ['admin', 'operator'].includes(user.role)) {
      loadInstitutions()
    }
  }, [user?.role])

  const getBillingTypeLabel = (type: string) => {
    switch (type) {
      case 'segment': return 'Segmente'
      case 'hour': return 'Ore'
      default: return type
    }
  }

  const getBillingTypeColor = (type: string) => {
    switch (type) {
      case 'segment': return 'secondary'
      case 'hour': return 'primary'
      default: return 'default'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'În așteptare'
      case 'approved': return 'Aprobat'
      case 'rejected': return 'Respins'
      case 'paid': return 'Plătit'
      default: return status
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'warning'
      case 'approved': return 'success'
      case 'rejected': return 'error'
      case 'paid': return 'info'
      default: return 'default'
    }
  }

  return (
    <Box p={3}>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Instituție filter - pentru medici, înainte de Data început */}
          {user?.role === 'medic' && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Instituție</InputLabel>
                <Select
                  value={institutionFilter}
                  onChange={(e) => setInstitutionFilter(e.target.value)}
                  label="Instituție"
                >
                  <MenuItem value="">Toate instituțiile</MenuItem>
                  {institutions.map((institution) => (
                    <MenuItem key={institution.id} value={institution.id}>
                      {institution.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          
          <Grid item xs={12} md={user?.role === 'medic' ? 3 : 3}>
            <TextField
              fullWidth
              label="Data început"
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} md={user?.role === 'medic' ? 3 : 3}>
            <TextField
              fullWidth
              label="Data sfârșit"
              type="date"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          {/* Instituție filter - pentru admin/operator, după date */}
          {user?.role && ['admin', 'operator'].includes(user.role) && (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Instituție</InputLabel>
                <Select
                  value={institutionFilter}
                  onChange={(e) => setInstitutionFilter(e.target.value)}
                  label="Instituție"
                >
                  <MenuItem value="">Toate instituțiile</MenuItem>
                  {institutions.map((institution) => (
                    <MenuItem key={institution.id} value={institution.id}>
                      {institution.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          
          <Grid item xs={12} md={user?.role === 'medic' ? 3 : 2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadDashboard}
              fullWidth
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {dashboardData ? (
        <>
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        În așteptare
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {dashboardData.overall_stats.count_draft}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Number(dashboardData.overall_stats.total_draft).toFixed(2)} RON
                      </Typography>
                    </Box>
                    <ScheduleIcon color="warning" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Aprobate
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {dashboardData.overall_stats.count_approved}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Number(dashboardData.overall_stats.total_approved).toFixed(2)} RON
                      </Typography>
                    </Box>
                    <AssignmentIcon color="success" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Plătite
                      </Typography>
                      <Typography variant="h4" color="primary.main">
                        {dashboardData.overall_stats.count_paid}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Number(dashboardData.overall_stats.total_paid).toFixed(2)} RON
                      </Typography>
                    </Box>
                    <MoneyIcon color="primary" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Total Câștiguri
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {(Number(dashboardData.overall_stats.total_approved) + Number(dashboardData.overall_stats.total_paid)).toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        RON
                      </Typography>
                    </Box>
                    <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Billing entries with pagination */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Plăți ({dashboardData.total_entries} total)
              </Typography>
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tip</TableCell>
                    <TableCell>Instituție</TableCell>
                    <TableCell>Descriere</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Sumă</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboardData.recent_entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <Chip
                          label={getBillingTypeLabel(entry.billing_type)}
                          color={getBillingTypeColor(entry.billing_type) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {entry.institution_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {entry.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(entry.billing_date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {typeof entry.total_amount === 'number' ? entry.total_amount.toFixed(2) : 
                           (isNaN(parseFloat(entry.total_amount)) ? '0.00' : parseFloat(entry.total_amount).toFixed(2))} {entry.currency}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(entry.status)}
                          color={getStatusColor(entry.status) as any}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {dashboardData.recent_entries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Nu există plăți în perioada selectată
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={dashboardData.total_entries}
              page={page}
              onPageChange={(_event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage="Plăți per pagină:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} din ${count !== -1 ? count : `mai mult de ${to}`}`
              }
            />
          </Paper>
        </>
      ) : (
        <Alert severity="info">
          Selectați o perioadă pentru a vizualiza dashboard-ul.
        </Alert>
      )}
    </Box>
  )
}

export default BillingDashboard
